
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, CheckSquare, FileCheck } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { isNonFreeEmail } from "@/utils/forms/contactFormSchema";
import ChecklistCTA from "./ChecklistCTA";
import { contactFormSchema, ContactFormValues } from "@/utils/forms/contactFormSchema";

interface ChecklistDownloadProps {
  className?: string;
}

const ChecklistDownload: React.FC<ChecklistDownloadProps> = ({ className = "" }) => {
  const [showForm, setShowForm] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const checklistUrl = "https://qcgnklqgyzwvjrmhzwfj.supabase.co/storage/v1/object/sign/insights/Board-AI-Governance-Checklist%20(8).pdf?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6InN0b3JhZ2UtdXJsLXNpZ25pbmcta2V5XzM0NzQ3N2ExLTRmMzEtNGE0ZC1hYTdmLWJiNGVhYzFhNzMwYiJ9.eyJ1cmwiOiJpbnNpZ2h0cy9Cb2FyZC1BSS1Hb3Zlcm5hbmNlLUNoZWNrbGlzdCAoOCkucGRmIiwiaWF0IjoxNzQ3MTY2MDEyLCJleHAiOjE3Nzg3MDIwMTJ9.suzFapPZ8o4ULlbHGtmYY4tzMcdqv9e1s-qVDAf9Fio";

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      title: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    
    try {
      // Store the user information in Supabase
      const { error } = await supabase.from("contacts").insert({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        company: data.company,
        title: data.title,
        lead_list_name: "checklist_download",
        submitted_at: new Date().toISOString() // Add timestamp
      });

      if (error) throw error;
      
      // Show the download
      setDownloadReady(true);
      setShowForm(false);
      
      toast({
        title: "Success!",
        description: "Your checklist is ready to download.",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      
      // In development or if there's an error, still provide the download
      setDownloadReady(true);
      setShowForm(false);
      
      toast({
        title: "Download Ready",
        description: "Your checklist is available for download.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = () => {
    try {
      // Create an anchor element and set the href attribute to the file URL
      const link = document.createElement('a');
      link.href = checklistUrl;
      link.download = "Board-AI-Governance-Checklist.pdf"; // Set the download attribute
      document.body.appendChild(link); // Append to the body
      link.click(); // Trigger the download
      document.body.removeChild(link); // Remove the link from the body
      
      toast({
        title: "Download started",
        description: "Your checklist download has started",
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download failed",
        description: "There was a problem downloading the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className={`w-full flex flex-col items-center h-full ${className}`}>
      {!downloadReady ? (
        <div className="bg-gradient-to-br from-white to-techne-lightestGray rounded-xl shadow-xl border border-gray-100 p-0 w-full overflow-hidden animate-fade-in h-full">
          {/* Top Banner */}
          <div className="bg-gradient-to-r from-techne-purple to-techne-primaryPurple text-white py-2 px-4 text-sm font-medium flex justify-between items-center">
            <span>BOARD GOVERNANCE TOOLKIT</span>
            <span className="flex items-center gap-1">
              <CheckSquare className="h-3 w-3" /> Interactive Checklist
            </span>
          </div>
          
          <div className="p-8 flex flex-col h-full">
            {/* Image */}
            <div className="mb-4">
              <div className="rounded-lg overflow-hidden shadow-lg border border-gray-200 mb-4 transform transition-transform hover:scale-105">
                <img 
                  src="/lovable-uploads/c399fa49-c31b-4eae-a43c-ba46e2d42347.png" 
                  alt="AI Governance Checklist" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex flex-col flex-grow">
              <h2 className="text-2xl font-bold leading-tight">
                <span className="text-techne-primaryPurple block mb-1">Download:</span>
                Board AI Governance Checklist
              </h2>
              
              <h3 className="text-lg text-gray-700 font-medium mb-4">Comprehensive Framework for Effective Oversight</h3>
              
              <div className="bg-white/80 rounded-lg p-4 mb-6 border-l-4 border-techne-purple shadow-sm flex-grow">
                <p className="text-gray-700 font-medium mb-3">What's included:</p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex gap-2">
                    <CheckSquare className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>40+ actionable governance checkpoints</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckSquare className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>Risk assessment framework for AI initiatives</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckSquare className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />
                    <span>Board-level responsibility matrix</span>
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={() => setShowForm(true)}
                size="lg"
                className="w-full md:w-auto bg-techne-purple hover:bg-techne-deepPurple text-white transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 mt-auto"
              >
                <Download className="h-4 w-4" />
                Download Checklist
              </Button>
              
              <p className="text-xs text-gray-500 mt-3 text-center md:text-left">
                Enter your business email to receive this board-level governance checklist.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="animate-fade-in w-full h-full">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full">
            <div className="bg-gradient-to-r from-techne-purple to-techne-primaryPurple py-5 px-6">
              <h2 className="text-white text-xl font-semibold">Your Board AI Governance Checklist is Ready</h2>
              <p className="text-white/90 text-sm">Click the button below to download</p>
            </div>
            <div className="p-6 text-center h-full flex flex-col">
              <p className="text-gray-700 mb-6">
                Thank you for your interest in improving AI governance at the board level. 
                Your checklist is now ready for download.
              </p>
              <Button 
                onClick={handleDownload}
                size="lg"
                className="bg-techne-purple hover:bg-techne-deepPurple text-white transition-all duration-300 shadow-md hover:shadow-lg flex items-center mx-auto justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Download Checklist Now
              </Button>
              
              <div className="mt-8 pt-6 border-t border-gray-200 mt-auto">
                <ChecklistCTA />
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download AI Governance Checklist</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corporation" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Role</FormLabel>
                    <FormControl>
                      <Input placeholder="Board Member, CEO, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : "Download Checklist"}
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                By submitting, you agree to our privacy policy and consent to receive communications from us.
              </p>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistDownload;
